// pages/DestinationForm.js
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MediaService from "../../../services/KangarooServices/MediaServices";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { imageBaseApi } from "../../../utilis/imageHelper";
import MediaModal from "../../PagesSection/Component/MediaModal";
import DestinationService from "../../../services/KangarooServices/DestinationService";
import * as Yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import ColorPicker from "../../../Components/ColorPicker/ColorPicker";

const DestinationForm = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("1");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState();
  const navigate = useNavigate();
  // Media Modal State
  const [mediaModalState, setMediaModalState] = useState({
    isOpen: false,
    targetField: "",
    index: null, // only for subDetails
  });

  const toggleMediaModal = (targetField = "", index = null) => {
    setMediaModalState({
      isOpen: !mediaModalState.isOpen,
      targetField,
      index,
    });
  };
  useEffect(() => {
    const fetchDetail = async () => {
      setIsLoading(true);
      try {
        const response = await DestinationService.getBySlug(id);
        console.log("🚀 ~ fetchDetail ~ response:", response);
        setData(response);
      } catch (error) {
        console.log(error);
        toast.error("Failed to fetch destination details");
        navigate("/destination-countries");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchDetail();
    }
  }, [id, navigate]);
  // -----------------------------
  // FORMIK
  // -----------------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || "",

      flagMediaId: data?.flagMedia?.id || "",
      flagMedia: data?.flagMedia || null,

      bannerMediaId: data?.bannerMedia?.id || "",
      bannerMedia: data?.bannerMedia || null,

      bannerText: data?.bannerText || "",
      bannerButtonText: data?.bannerButtonText || "",

      mainDescription: data?.mainDescription || "",
      description: data?.description || "",

      studyBenefits: data?.studyBenefits || "",
      visaRequirements: data?.visaRequirements || "",
      costOfLiving: data?.costOfLiving || "",
      partTimeWork: data?.partTimeWork || "",
      postStudyWork: data?.postStudyWork || "",
      bannerTextColorCode: data?.bannerTextColorCode || "#000",
      isActive: data?.isActive ?? true,
      isIncludedInIntelligentPortal:
        data?.isIncludedInIntelligentPortal || false,
      orderIndex: data?.orderIndex || 0,
      videoUrl: data?.videoUrl || "",

      intelligentPortalBannerText: data?.intelligentPortalBannerText || "",
      intelligentPortalBannerId: data?.intelligentBannerMedia?.id || "",
      intelligentBannerMedia: data?.intelligentBannerMedia || "",
      subDetails:
        data?.subDetails?.map((d, index) => ({
          id: d.id, // IMPORTANT for update
          title: d.title || "",
          description: d.description || "",
          mediaId: d.media?.id || "",
          media: d.media || null, // UI only
          link: d.link || "",
          orderIndex: d.orderIndex ?? index,
        })) || [],
    },

    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        flagMediaId: values.flagMediaId || null,
        bannerMediaId: values.bannerMediaId || null,
        bannerText: values.bannerText,
        bannerButtonText: values.bannerButtonText,
        mainDescription: values.mainDescription,
        description: values.description,
        isIncludedInIntelligentPortal: values?.isIncludedInIntelligentPortal,
        intelligentPortalBannerText: values?.intelligentPortalBannerText || "",
        intelligentPortalBannerId: values?.intelligentPortalBannerId || "",
        isActive: values.isActive,
        orderIndex: values.orderIndex,
        videoUrl: values?.videoUrl,
        bannerTextColorCode: values?.bannerTextColorCode,
        subDetails: values.subDetails.map((d, index) => ({
          id: d.id, // keep id for update
          title: d.title,
          description: d.description,
          mediaId: d.mediaId || null,
          link: d.link,
          orderIndex: index,
        })),
      };

      try {
        if (data?.id) {
          await DestinationService.updateDestination(data?.id, payload);
          toast.success("Destination updated successfully");
        } else {
          await DestinationService.createDestination(payload);
          toast.success("Destination created successfully");
        }
        navigate("/destination-countries");
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong!");
      }
    },
  });

  const { values, setFieldValue, handleChange, handleSubmit } = formik;

  // -----------------------------
  // SUB DETAIL HANDLERS
  // -----------------------------
  const addSubDetail = () => {
    setFieldValue("subDetails", [
      ...values.subDetails,
      {
        title: "",
        description: "",
        mediaId: "",
        media: null,
        link: "",
        orderIndex: values.subDetails.length,
      },
    ]);
  };

  const removeSubDetail = (index) => {
    const updated = values.subDetails.filter((_, i) => i !== index);
    setFieldValue("subDetails", updated);
  };

  const updateSubDetail = (index, field, value) => {
    const updated = [...values.subDetails];
    updated[index][field] = value;
    setFieldValue("subDetails", updated);
  };

  // -----------------------------
  // MEDIA SELECT HANDLER
  // -----------------------------
  const handleMediaSelect = (media) => {
    const { targetField, index } = mediaModalState;

    if (targetField === "flag") {
      setFieldValue("flagMediaId", media.id);
      setFieldValue("flagMedia", media);
    }

    if (targetField === "banner") {
      setFieldValue("bannerMediaId", media.id);
      setFieldValue("bannerMedia", media);
    }
    if (targetField === "intelligentBannerMedia") {
      setFieldValue("intelligentPortalBannerId", media.id);
      setFieldValue("intelligentBannerMedia", media);
    }
    if (targetField === "subDetail" && index !== null) {
      const updated = [...values.subDetails];
      updated[index].mediaId = media.id;
      updated[index].media = media;
      setFieldValue("subDetails", updated);
    }

    toggleMediaModal();
  };

  const breadcrumbItems = [
    { title: "Back to List", link: "/destination-countries" },
  ];
  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Destination"
            pageTitle="Destination Countries"
            breadcrumbItems={breadcrumbItems}
          />
          {/* Header */}
          <Row className="mb-3">
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-end">
                <div className="d-flex gap-2">
                  <Button
                    color="danger"
                    onClick={() => navigate("/destination-countries")}
                  >
                    Cancel
                  </Button>

                  <Button color="success" onClick={handleSubmit}>
                    Publish
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            {/* MAIN AREA */}
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <Nav tabs className="card-header-tabs">
                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        Basic Info
                      </NavLink>
                    </NavItem>

                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => setActiveTab("2")}
                      >
                        Banner
                      </NavLink>
                    </NavItem>

                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => setActiveTab("3")}
                      >
                        Sub Details
                      </NavLink>
                    </NavItem>
                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "4" ? "active" : ""}
                        onClick={() => setActiveTab("4")}
                      >
                        Intelligence Info
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>

                <CardBody>
                  <TabContent activeTab={activeTab}>
                    {/* TAB 1 */}
                    <TabPane tabId="1">
                      <FormGroup>
                        <Label>Name</Label>
                        <Input
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Order Index</Label>
                        <Input
                          type="number"
                          name="orderIndex"
                          value={values.orderIndex}
                          onChange={handleChange}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Main Description</Label>

                        <CKEditor
                          editor={ClassicEditor}
                          data={values.mainDescription}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setFieldValue("mainDescription", data); // Formik's setFieldValue
                          }}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Description</Label>

                        <CKEditor
                          editor={ClassicEditor}
                          data={values.description}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setFieldValue("description", data); // Formik's setFieldValue
                          }}
                        />
                      </FormGroup>
                    </TabPane>

                    {/* TAB 3: Banner */}
                    <TabPane tabId="2">
                      <FormGroup>
                        <Label>Banner Text</Label>
                        <Input
                          name="bannerText"
                          value={values.bannerText}
                          onChange={handleChange}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Banner Button Text</Label>
                        <Input
                          name="bannerButtonText"
                          value={values.bannerButtonText}
                          onChange={handleChange}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Banner Text Color</Label>

                        <ColorPicker
                          value={values.bannerTextColorCode || "#000"}
                          onChange={(color) =>
                            setFieldValue("bannerTextColorCode", color)
                          }
                          isToggle={false}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label className="fw-semibold mb-2">Banner Media</Label>

                        {values.bannerMedia ? (
                          <div className="text-center p-3 border rounded shadow-sm bg-light">
                            {/* Image Preview */}
                            <div
                              style={{
                                maxHeight: "220px",
                                overflow: "hidden",
                                borderRadius: "10px",
                                border: "1px solid #ddd",
                              }}
                              className="mb-3"
                            >
                              <img
                                src={`${imageBaseApi}${values.bannerMedia.url}`}
                                alt="Banner"
                                style={{ width: "100%", objectFit: "cover" }}
                                className="img-fluid hover-zoom"
                              />
                            </div>

                            {/* Image Info */}
                            <div className="mb-2">
                              <div className="fw-semibold">
                                {values.bannerMedia.name || "Banner Image"}
                              </div>

                              {values.bannerMedia.size && (
                                <small className="text-muted">
                                  Size:{" "}
                                  {(values.bannerMedia.size / 1024).toFixed(1)}{" "}
                                  KB
                                </small>
                              )}
                            </div>

                            {/* Buttons */}
                            <div className="d-flex gap-2 justify-content-center">
                              <Button
                                size="sm"
                                outline
                                color="primary"
                                className="rounded-pill px-3"
                                onClick={() => toggleMediaModal("banner")}
                              >
                                <i className="ri-edit-line"></i>
                              </Button>

                              <Button
                                size="sm"
                                outline
                                color="danger"
                                className="rounded-pill px-3"
                                onClick={() => {
                                  setFieldValue("bannerMediaId", "");
                                  setFieldValue("bannerMedia", null);
                                }}
                              >
                                <i className="ri-delete-bin-5-line me-1"></i>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 border rounded text-center bg-light">
                            <i className="ri-image-add-line fs-1 text-secondary"></i>
                            <h6 className="mt-2 mb-3 text-muted">
                              No banner image selected
                            </h6>
                            <h6 className="mb-2 text-muted">2400 x 400</h6>

                            <Button
                              outline
                              color="secondary"
                              className="rounded-pill px-4"
                              onClick={() => toggleMediaModal("banner")}
                            >
                              <i className="ri-image-add-line me-1"></i> Choose
                              Banner Image
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </TabPane>

                    {/* TAB 4: Sub Details */}
                    <TabPane tabId="3">
                      <Button color="success" size="sm" onClick={addSubDetail}>
                        + Add Sub Detail
                      </Button>

                      <div className="mt-3 vstack gap-3">
                        {values?.subDetails?.map((d, index) => (
                          <Card key={d.id} className="h-100">
                            <CardBody>
                              <FormGroup>
                                <div className="d-flex justify-content-between mb-2">
                                  <Label>Title</Label>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    onClick={() => removeSubDetail(index)}
                                  >
                                    <i className="ri-delete-bin-5-line"></i>
                                  </Button>
                                </div>
                                <Input
                                  value={d.title}
                                  onChange={(e) =>
                                    updateSubDetail(
                                      index,
                                      "title",
                                      e.target.value,
                                    )
                                  }
                                />
                              </FormGroup>

                              <FormGroup>
                                <Label>Description</Label>

                                <CKEditor
                                  editor={ClassicEditor}
                                  data={d.description} // FIXED!
                                  onChange={(event, editor) => {
                                    const data = editor.getData();
                                    updateSubDetail(index, "description", data); // FIXED!
                                  }}
                                />
                              </FormGroup>

                              <FormGroup>
                                <Label className="fw-semibold mb-2">
                                  Media
                                </Label>

                                {d.media ? (
                                  <div className="d-flex align-items-center gap-3 p-3 border rounded shadow-sm bg-light">
                                    {/* Thumbnail */}
                                    <div
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        border: "1px solid #ddd",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <img
                                        src={`${imageBaseApi}${d.media.url}`}
                                        alt="media"
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          transition: "transform .2s",
                                        }}
                                        className="hover-zoom"
                                      />
                                    </div>

                                    {/* Media Info + Actions */}
                                    <div className="d-flex flex-column justify-content-between">
                                      <div>
                                        <div className="fw-semibold">
                                          {d.media.name || "Media File"}
                                        </div>

                                        {/* SIZE INFO */}
                                        {d.media.size && (
                                          <small className="text-muted">
                                            Size:{" "}
                                            {(d.media.size / 1024)?.toFixed(1)}{" "}
                                            KB
                                          </small>
                                        )}
                                      </div>

                                      <div className="d-flex gap-2 mt-2">
                                        <Button
                                          size="sm"
                                          color="primary"
                                          outline
                                          className="rounded-pill px-3"
                                          onClick={() =>
                                            toggleMediaModal("subDetail", index)
                                          }
                                        >
                                          Change
                                        </Button>

                                        <Button
                                          size="sm"
                                          color="danger"
                                          outline
                                          className="rounded-pill px-3"
                                          onClick={() => {
                                            updateSubDetail(
                                              index,
                                              "mediaId",
                                              "",
                                            );
                                            updateSubDetail(
                                              index,
                                              "media",
                                              null,
                                            );
                                          }}
                                        >
                                          <i className="ri-delete-bin-5-line me-1"></i>
                                          Remove
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center p-4 border rounded bg-light">
                                    <i className="ri-image-add-line fs-1 text-secondary"></i>
                                    <h6 className="mt-2 mb-2 text-muted">
                                      No media added
                                    </h6>
                                    <h6 className="mb-2 text-muted">
                                      800 x 800
                                    </h6>

                                    <Button
                                      outline
                                      size="sm"
                                      color="secondary"
                                      className="rounded-pill px-3"
                                      onClick={() =>
                                        toggleMediaModal("subDetail", index)
                                      }
                                    >
                                      <i className="ri-image-add-line me-1"></i>{" "}
                                      Add Media
                                    </Button>
                                  </div>
                                )}
                              </FormGroup>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    </TabPane>

                    <TabPane tabId="4">

                      <FormGroup>
                        <Label>Intelligent Portal Banner Text</Label>
                        <Input
                          name="intelligentPortalBannerText"
                          value={values.intelligentPortalBannerText}
                          onChange={handleChange}
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label className="fw-semibold mb-2">
                          Intelligence Banner Media
                        </Label>

                        {values.intelligentBannerMedia ? (
                          <div className="text-center p-3 border rounded shadow-sm bg-light">
                            {/* Image Preview */}
                            <div
                              style={{
                                maxHeight: "220px",
                                overflow: "hidden",
                                borderRadius: "10px",
                                border: "1px solid #ddd",
                              }}
                              className="mb-3"
                            >
                              <img
                                src={`${imageBaseApi}${values.intelligentBannerMedia.url}`}
                                alt="Banner"
                                style={{ width: "100%", objectFit: "cover" }}
                                className="img-fluid hover-zoom"
                              />
                            </div>

                            {/* Image Info */}
                            <div className="mb-2">
                              <div className="fw-semibold">
                                {values.intelligentBannerMedia.name ||
                                  "Banner Image"}
                              </div>

                              {values.intelligentBannerMedia.size && (
                                <small className="text-muted">
                                  Size:{" "}
                                  {(
                                    values.intelligentBannerMedia.size / 1024
                                  ).toFixed(1)}{" "}
                                  KB
                                </small>
                              )}
                            </div>

                            {/* Buttons */}
                            <div className="d-flex gap-2 justify-content-center">
                              <Button
                                size="sm"
                                outline
                                color="primary"
                                className="rounded-pill px-3"
                                onClick={() =>
                                  toggleMediaModal("intelligentBannerMedia")
                                }
                              >
                                <i className="ri-edit-line"></i>
                              </Button>

                              <Button
                                size="sm"
                                outline
                                color="danger"
                                className="rounded-pill px-3"
                                onClick={() => {
                                  setFieldValue(
                                    "intelligentPortalBannerId",
                                    "",
                                  );
                                  setFieldValue("intelligentBannerMedia", null);
                                }}
                              >
                                <i className="ri-delete-bin-5-line me-1"></i>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 border rounded text-center bg-light">
                            <i className="ri-image-add-line fs-1 text-secondary"></i>
                            <h6 className="mt-2 mb-3 text-muted">
                              No banner image selected
                            </h6>
                            <h6 className="mb-2 text-muted">2400 x 400</h6>

                            <Button
                              outline
                              color="secondary"
                              className="rounded-pill px-4"
                              onClick={() =>
                                toggleMediaModal("intelligentBannerMedia")
                              }
                            >
                              <i className="ri-image-add-line me-1"></i> Choose
                              Banner Image
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>

            {/* SIDEBAR */}
            <Col lg={4}>
              <Card className="shadow-sm">
                <CardHeader className="fw-semibold">Status</CardHeader>
                <CardBody>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    {/* Label */}
                    <Label className="mb-0 fw-medium">Is Active?</Label>

                    {/* Toggle Switch (Checkbox) */}
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={values.isActive}
                        onChange={(e) =>
                          setFieldValue("isActive", e.target.checked)
                        }
                      />
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3 mb-3">
                    {/* Label */}
                    <Label className="mb-0 fw-medium">
                      Is included in intelligent portal?
                    </Label>

                    {/* Toggle Switch (Checkbox) */}
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={values.isIncludedInIntelligentPortal}
                        onChange={(e) =>
                          setFieldValue(
                            "isIncludedInIntelligentPortal",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                  </div>
                  <FormGroup>
                    <Label>Video Url</Label>
                    <Input
                      name="videoUrl"
                      value={values.videoUrl}
                      onChange={(e) =>
                        setFieldValue("videoUrl", e.target.value)
                      }
                    />
                  </FormGroup>
                </CardBody>
              </Card>

              {/* FLAG MEDIA */}
              <Card className="shadow-sm">
                <CardHeader className="fw-semibold">Flag Media</CardHeader>

                <CardBody>
                  {values?.flagMedia ? (
                    <div className="text-center p-2">
                      {/* Flag Preview Box */}
                      <div
                        style={{
                          maxHeight: "150px",
                          width: "200px",
                          margin: "0 auto",
                          overflow: "hidden",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                        }}
                        className="mb-3"
                      >
                        <img
                          src={`${imageBaseApi}${values?.flagMedia.url}`}
                          alt="Flag"
                          style={{ width: "100%", objectFit: "contain" }}
                          className="img-fluid hover-zoom"
                        />
                      </div>

                      {/* Media Info */}
                      <div className="mb-2">
                        <div className="fw-semibold">
                          {values.flagMedia.name || "Flag Image"}
                        </div>

                        {values.flagMedia.size && (
                          <small className="text-muted">
                            Size: {(values.flagMedia.size / 1024).toFixed(1)} KB
                          </small>
                        )}
                      </div>

                      {/* Buttons */}
                      <div className="d-flex justify-content-center gap-2 mt-2">
                        <Button
                          outline
                          size="sm"
                          color="primary"
                          className="rounded-pill px-3"
                          onClick={() => toggleMediaModal("flag")}
                        >
                          <i className="ri-edit-line"></i>
                        </Button>

                        <Button
                          outline
                          size="sm"
                          color="danger"
                          className="rounded-pill px-3"
                          onClick={() => {
                            setFieldValue("flagMediaId", "");
                            setFieldValue("flagMedia", null);
                          }}
                        >
                          <i className="ri-delete-bin-5-line me-1"></i>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-3">
                      <i className="ri-flag-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-3 text-muted">No flag selected</h6>
                      <h6 className="mb-2 text-muted">800 x 800</h6>

                      <Button
                        outline
                        size="sm"
                        color="secondary"
                        className="rounded-pill px-3"
                        onClick={() => toggleMediaModal("flag")}
                      >
                        <i className="ri-image-add-line me-1"></i> Choose Flag
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* MEDIA MODAL - SINGLE INSTANCE */}
      <MediaModal
        isOpen={mediaModalState.isOpen}
        toggle={toggleMediaModal}
        selectedMedia={null} // NEW → MediaModal needs this
        setSelectedMedia={handleMediaSelect} // NEW → MediaModal calls this on Add button
      />
    </>
  );
};

export default DestinationForm;
